import React, { useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDocumentNumberV2 } from './hooks/useDocumentNumberV2';
import { quyenChungTuSearchColumns } from '@/constants';
import type { DocumentNumberFieldProps } from './types';
import type { QuyenChungTu } from '@/types/schemas';
import { SearchFieldV2 } from '../search-field-v2';
import { Label } from '@/components/ui/label';

export const DocumentNumberFieldV2: React.FC<DocumentNumberFieldProps> = ({
  ma_ct,
  dateFieldName = 'ngay_ct',
  onQuyenChungTuChange,
  quyenChungTu,
  soChungTu,
  onSoChungTuChange,
  disabled = false,
  label = 'Số chứng từ',
  labelClassName = 'w-32 min-w-32',
  className = 'flex items-center',
  classNameSearchField,
  formMode = 'add',
  name
}) => {
  const formContext = useFormContext();
  const {
    quyenChungTus,
    soChungTu: hookSoChungTu,
    setSoChungTu: setHookSoChungTu,
    setQuyenChungTu: setHookQuyenChungTu,
    refreshQuyenChungTus
  } = useDocumentNumberV2({
    ma_ct,
    dateFieldName,
    initialQuyenChungTu: quyenChungTu ?? null,
    initialSoChungTu: soChungTu,
    formMode
  });

  const displaySoChungTu = hookSoChungTu || soChungTu || '';
  const onQuyenChungTuChangeRef = useRef(onQuyenChungTuChange);
  const onSoChungTuChangeRef = useRef(onSoChungTuChange);
  const lastEmittedSoChungTuRef = useRef(soChungTu || '');
  const autoSelectedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    onQuyenChungTuChangeRef.current = onQuyenChungTuChange;
  }, [onQuyenChungTuChange]);

  useEffect(() => {
    onSoChungTuChangeRef.current = onSoChungTuChange;
  }, [onSoChungTuChange]);

  useEffect(() => {
    lastEmittedSoChungTuRef.current = soChungTu || '';
  }, [soChungTu]);

  const emitSoChungTuChange = useCallback(
    (nextSoChungTu: string) => {
      if (lastEmittedSoChungTuRef.current === nextSoChungTu) {
        return;
      }

      lastEmittedSoChungTuRef.current = nextSoChungTu;
      onSoChungTuChangeRef.current(nextSoChungTu);

      if (name && formContext) {
        formContext.setValue(name, nextSoChungTu, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
      }
    },
    [formContext, name]
  );

  const handleQuyenChungTuChange = useCallback(
    (selectedQuyenChungTu: QuyenChungTu | null) => {
      if (!selectedQuyenChungTu) {
        setHookQuyenChungTu(null);
        return;
      }

      const generatedSoChungTu = setHookQuyenChungTu(selectedQuyenChungTu);
      onQuyenChungTuChangeRef.current?.(selectedQuyenChungTu);
      if (generatedSoChungTu) {
        emitSoChungTuChange(generatedSoChungTu);
      }
    },
    [emitSoChungTuChange, setHookQuyenChungTu]
  );

  const handleSoChungTuChange = useCallback(
    (newValue: string) => {
      setHookSoChungTu(newValue);
      emitSoChungTuChange(newValue);
    },
    [emitSoChungTuChange, setHookSoChungTu]
  );

  useEffect(() => {
    if (hookSoChungTu) {
      emitSoChungTuChange(hookSoChungTu);
    }
  }, [emitSoChungTuChange, hookSoChungTu]);

  useEffect(() => {
    const firstQuyenChungTu = quyenChungTus?.[0];
    const firstQuyenChungTuKey = firstQuyenChungTu?.uuid || firstQuyenChungTu?.ma_nk || null;

    if (
      formMode === 'add' &&
      firstQuyenChungTu &&
      firstQuyenChungTuKey &&
      autoSelectedKeyRef.current !== `${ma_ct}:${firstQuyenChungTuKey}`
    ) {
      autoSelectedKeyRef.current = `${ma_ct}:${firstQuyenChungTuKey}`;
      handleQuyenChungTuChange(quyenChungTus[0]);
    }
  }, [formMode, handleQuyenChungTuChange, ma_ct, quyenChungTus]);

  return (
    <div className={className}>
      <Label className={labelClassName}>{label}</Label>
      <div className={classNameSearchField}>
        <SearchFieldV2<QuyenChungTu>
          type='text'
          name={name}
          errorDisplay='tooltip'
          value={displaySoChungTu}
          onValueChange={handleSoChungTuChange}
          searchColumns={quyenChungTuSearchColumns}
          disabled={disabled}
          dialogTitle='Danh mục quyển'
          columnDisplay='ma_nk'
          rows={quyenChungTus}
          onRowSelection={handleQuyenChungTuChange}
          onBeforeOpen={async () => {
            await refreshQuyenChungTus?.();
          }}
        />
      </div>
    </div>
  );
};

export { useDocumentNumberV2 as DocumentNumberHookV2 } from './hooks/useDocumentNumberV2';
export type { DocumentNumberFieldProps, UseDocumentNumberProps, UseDocumentNumberReturn } from './types';
